import React, { useState, useContext, useEffect } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import './ChapterEditor.scss';

export const ShapeComponent = ({
    content,
    onContentChange,
    isSelected,
    onSelect,
    id,
    borderRadius,
    backgroundColor = '#000000',
    opacity = 1,
    mode = "edit",
    // Shape-specific properties
    shapeType = 'rectangle',
    borderColor = '#000000',
    borderWidth = 0,
    onAnimationComplete,
    isSequentialAnimationActive = false,
    onOpenEditorDrawer
}) => {
    const { i18n } = useContext(I18nReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const [selectedShape, setSelectedShape] = useState(shapeType);
    const [isAIAnimating, setIsAIAnimating] = useState(false);

    // Sync local state with prop changes from EditorDrawer
    useEffect(() => {
        setSelectedShape(shapeType);
    }, [shapeType]);

    // AI Shape Animation Effect
    useEffect(() => {
        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
        const isShowingAIAnimations = courseEditorContext?.isShowingAIChapterAnimations;

        // Only animate if (AI is generating OR showing AI chapter animations OR sequential animations are active) and we're not in edit mode
        if ((isAIGenerating || isShowingAIAnimations || isSequentialAnimationActive) && mode !== "edit") {
            setIsAIAnimating(true);

            // For sequential animations during AI generation, trigger completion almost immediately
            // For other cases, use the full 2-second animation duration
            const completionDelay = isSequentialAnimationActive ? 200 : 2000;

            // Stop animation after the appropriate delay
            const animationTimeout = setTimeout(() => {
                setIsAIAnimating(false);
                // Call animation completion callback for sequential animations
                if (isSequentialAnimationActive && onAnimationComplete) {
                    console.log('ShapeComponent animation completed for:', id);
                    onAnimationComplete(id);
                }
            }, completionDelay);

            return () => {
                clearTimeout(animationTimeout);
            };
        } else {
            setIsAIAnimating(false);
            // If sequential animations are active but we're not showing animation,
            // immediately call completion callback
            if (isSequentialAnimationActive && onAnimationComplete) {
                console.log('ShapeComponent immediately completing for:', id);
                setTimeout(() => onAnimationComplete(id), 100);
            }
        }
    }, [courseEditorContext?.isGeneratingChapter, courseEditorContext?.isShowingAIChapterAnimations, mode, isSequentialAnimationActive, onAnimationComplete, id]);

    // Available shape types
    const shapeTypes = [
        { type: 'rectangle', name: i18n.translate('weezbeeCourse.editor.shapeComponent.rectangle', { defaultValue: 'Rectangle' }) },
        { type: 'circle', name: i18n.translate('weezbeeCourse.editor.shapeComponent.circle', { defaultValue: 'Circle' }) }
    ];

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isSelected) {
            onSelect(id);
        }
    };

    const handleDoubleClick = (e) => {
        e.stopPropagation();
        // Open editor drawer on double-click in edit mode
        if (mode === "edit" && onOpenEditorDrawer) {
            onOpenEditorDrawer({
                id,
                type: 'shape',
                content,
                shapeType,
                borderRadius,
                backgroundColor,
                opacity,
                borderColor,
                borderWidth
            });
        }
    };

    const handleShapeChange = (newShapeType) => {
        setSelectedShape(newShapeType);
        if (onContentChange) {
            onContentChange(undefined, { shapeType: newShapeType });
        }
    };

    // Parse border radius values or use defaults
    const topLeft = borderRadius?.topLeft || 0;
    const topRight = borderRadius?.topRight || 0;
    const bottomLeft = borderRadius?.bottomLeft || 0;
    const bottomRight = borderRadius?.bottomRight || 0;

    // Calculate inner border radius that accounts for border width
    // This prevents gaps between the inner shape and the border frame
    const calculateInnerBorderRadius = (outerRadius, borderWidth) => {
        return Math.max(0, outerRadius - borderWidth);
    };

    // Create style object for the container (no borders - borders are now on the inner shape)
    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
    };

    // Create style for the container background (not the shape itself)
    // This is only used if the component wrapper needs a background
    const backgroundStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
        zIndex: -1,
    };

    // Create style for the shape
    const getShapeStyle = () => {
        const baseStyle = {
            opacity: opacity,
            position: 'relative',
            backgroundColor: backgroundColor,
            // Remove border styling from shape element to prevent double borders
            // Borders are handled by the border div
            border: 'none',
        };

        switch (selectedShape) {
            case 'circle':
                // For all borders, we need to make the main shape smaller to accommodate border space
                const hasBorder = borderWidth > 0;
                const circleSize = hasBorder 
                    ? `calc(100% - ${borderWidth * 2}px)` 
                    : '100%';
                
                return {
                    ...baseStyle,
                    // For circles, ensure perfect circle that fits within container bounds
                    // Adjust size based on border presence
                    width: circleSize,
                    height: circleSize,
                    // Maintain perfect 1:1 aspect ratio (square that becomes circle with border-radius)
                    aspectRatio: '1 / 1',
                    // Ensure circle doesn't exceed container bounds
                    maxWidth: '100%',
                    maxHeight: '100%',
                    // The aspect-ratio will automatically constrain to the smaller dimension
                    borderRadius: '50%',
                    minWidth: '40px',
                    minHeight: '40px',
                    // Ensure proper sizing within flexbox container
                    flexShrink: 1,
                    boxSizing: 'border-box',
                    // Center the smaller circle when using any border
                    ...(hasBorder && {
                        margin: `${borderWidth}px`,
                    }),
                };
            case 'rectangle':
            default:
                // For all borders, we need to inset the rectangle to make space for the border
                const hasBorderRect = borderWidth > 0;
                
                // Calculate inner border radius to prevent gaps between shape and border
                const innerTopLeft = hasBorderRect ? calculateInnerBorderRadius(topLeft, borderWidth) : topLeft;
                const innerTopRight = hasBorderRect ? calculateInnerBorderRadius(topRight, borderWidth) : topRight;
                const innerBottomLeft = hasBorderRect ? calculateInnerBorderRadius(bottomLeft, borderWidth) : bottomLeft;
                const innerBottomRight = hasBorderRect ? calculateInnerBorderRadius(bottomRight, borderWidth) : bottomRight;
                
                return {
                    ...baseStyle,
                    // Adjust size based on border presence - inset for all borders
                    width: hasBorderRect ? `calc(100% - ${borderWidth * 2}px)` : '100%',
                    height: hasBorderRect ? `calc(100% - ${borderWidth * 2}px)` : '100%',
                    // Apply calculated inner border radius to prevent gaps
                    borderRadius: `${innerTopLeft}px ${innerTopRight}px ${innerBottomRight}px ${innerBottomLeft}px`,
                    minWidth: '40px',
                    minHeight: '40px',
                    // Center the inset rectangle when using any border
                    ...(hasBorderRect && {
                        margin: `${borderWidth}px`,
                    }),
                };
        }
    };

    // Create border style for pseudo-element with proper masking
    const getBorderStyle = () => {
        if (borderWidth <= 0) {
            return null;
        }

        const borderStyleObj = {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: borderColor,
            // Use CSS mask to create border-only effect
            mask: `
                linear-gradient(#fff 0 0) content-box,
                linear-gradient(#fff 0 0)
            `,
            maskComposite: 'xor',
            WebkitMask: `
                linear-gradient(#fff 0 0) content-box,
                linear-gradient(#fff 0 0)
            `,
            WebkitMaskComposite: 'xor',
            padding: `${borderWidth}px`,
            boxSizing: 'border-box',
            pointerEvents: 'none',
        };

        // Apply border radius based on shape type
        if (selectedShape === 'circle') {
            borderStyleObj.borderRadius = '50%';
        } else {
            borderStyleObj.borderRadius = `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
        }

        return borderStyleObj;
    };

    const renderShape = () => {
        const shapeStyle = getShapeStyle();
        const borderStyle = getBorderStyle();
        
        return (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {/* Border background (supports both gradient and solid colors) */}
                {borderStyle && (
                    <div style={borderStyle} />
                )}
                {/* Main shape */}
                <div style={shapeStyle} />
            </div>
        );
    };

    return (
        <div 
            className={`ShapeComponent ${isSelected ? 'selected' : ''}`}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            style={{ width: '100%', height: '100%' }}
        >
            <div className={`shape-container ${isAIAnimating ? 'ai-image-creation' : ''}`} style={containerStyle}>
                {/* Background div with opacity */}
                <div style={backgroundStyle}></div>

                {/* Shape content */}
                {selectedShape ? (
                    renderShape()
                ) : mode !== "view" ? (
                    <div className="shape-placeholder">
                        <span>{i18n.translate('weezbeeCourse.editor.shapeComponent.emptyState', { defaultValue: 'Click to select a shape' })}</span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};
